import { ActionInboxPanel, } from './addin/action-inbox.js';
import { ICON_PROPS } from './addin/shared.js';
import { BriefPanel, InsightsPanel, RadarPanel } from './addin/briefing.js';
import { CalendarPanel, PeoplePanel } from './addin/calendar-people.js';
import { AuditPanel, CasesPanel, RulesPanel } from './addin/cases-rules-audit.js';
import { AddinLogin, BroadcastSurface, GlobalSearchInput, NotificationHistoryButton, OrganizeWithAIModal, QuickPalette, Sidebar, TabErrorBoundary } from './addin/chrome.js';
import { AskPanel, DiscoverPanel } from './addin/discover.js';
import { AskAiDrawer, AutopilotPanel, DraftReplyDrawer, FollowupsPanel, GoalsPanel, ReminderPanel, ReplyTemplatesPanel } from './addin/goals.js';
import { IndexPanel } from './addin/index-tab.js';
import { SearchPanel, TriagePanel } from './addin/search.js';
import { SettingsPanel } from './addin/settings.js';
import { type BridgeBus, type BridgeEvent, type IndexProgress, type IndexState, TabKeepAlive } from './addin/shared.js';
import { CommandBar, PrivateModeBadge, SenderProfileDrawer } from './addin/shared-components.js';
import { UpdateAvailableModal, UpdateBlockerOverlay } from './addin/update.js';
// /addin?ctx=addin route — modern Mantine UI hosted inside the COM addin's
// WebView2 control. Self-contained: no AppShell chrome, runs on its own
// authentication state, talks to Outlook via window.chrome.webview message
// bridge so search results can be opened in the real Outlook window and
// drafts can be inserted into a real Outlook reply.
//
// Round 27 — executive edition. New Insights tab is the default landing,
// search has smart-category chips + filters, brief has the executive
// summary treatment, index panel controls live indexing + auto-index +
// new-mail watch from React via the bridge.

import {
  Accordion,
  ActionIcon,
  Alert,
  AppShell,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Chip,
  Code,
  Collapse,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  List,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  NativeSelect,
  NavLink,
  NumberInput,
  Paper,
  PasswordInput,
  Progress,
  RingProgress,
  ScrollArea,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
  Tooltip,
  useMantineColorScheme,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArchive,
  IconBell,
  IconBolt,
  IconBrain,
  IconBriefcase,
  IconChartBar,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconClock,
  IconCommand,
  IconDeviceMobile,
  IconEdit,
  IconFileText,
  IconFlag,
  IconCalendar,
  IconDownload,
  IconFolder,
  IconGavel,
  IconHistory,
  IconMail,
  IconMailOff,
  IconMessageQuestion,
  IconMoonStars,
  IconNews,
  IconPaperclip,
  IconPencil,
  IconPhone,
  IconPlus,
  IconRadar,
  IconReload,
  IconReport,
  IconArrowBackUp,
  IconSchool,
  IconSearch,
  IconSend,
  IconSettings,
  IconShare,
  IconShield,
  IconSparkles,
  IconStar,
  IconStarFilled,
  IconTarget,
  IconTrash,
  IconUser,
  IconUserPlus,
  IconUsersGroup,
  IconWand,
  IconX,
} from '@tabler/icons-react';
import {
  type BulkQueueStatus,
  type HostMessage,
  type NotifHistEntry,
  type OutlookEmailContext,
  cleanSubject,
  formatDuration,
  formatRelative,
  friendlyAiError,
  notifHistory,
  openEmailInOutlook,
  parseHostMessage,
  persistNotifHistory,
  postToHost,
  runPreSendCheck,
  safeParse,
  sentimentColor,
  shortDate,
  TIER_COLOR,
  tierColor,
} from './addin/shared.js';
import { Component, type ErrorInfo, Fragment, type ReactNode, Suspense, lazy, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  type ActionInboxItem,
  type ActionInboxState,
  type AuthUser,
  api,
  clearAllAuth,
  getStoredToken,
  getStoredUser,
  setStoredToken,
  setStoredUser,
} from '../api.js';
// CommandCenterPage is heavy (~70 KB of kanban + timeline + decisions UI
// that many addin users never open). Lazy-import so Vite can chunk-split.
// Matches App.tsx's lazy import — without both being lazy, Vite collapses
// the chunk into the main bundle and emits a build warning.
const CommandCenterPage = lazy(() =>
  import('./CommandCenterPage.js').then((m) => ({ default: m.CommandCenterPage })),
);

// Round 28-U: catch render errors in tab subtrees so they don't blank the
// whole page. The followups tab was reported as "blank page only" — root
// cause was hidden because React's default behavior on a render throw is to
// unmount the entire subtree silently. Now we render the error inline.
export function AddinPage(): React.JSX.Element {
  const [token, setToken] = useState<string | null>(getStoredToken());
  const [user, setUser] = useState<AuthUser | null>(getStoredUser());
  // v3.6.2 followup (2026-06-20): api.ts dispatches this CustomEvent when
  // 5 consecutive 401s confirm the token is genuinely expired (vs a
  // transient blip). We flip local state to null which immediately
  // re-renders <AddinLogin>. Also push persist_signout so the C# side
  // clears its DPAPI-stored copy — otherwise reopen reads the same
  // expired token and we loop right back into 401 land.
  // First reported by rabih on 2026-06-20 after v3.6.2 install: C#-side
  // calls weren't 401'ing (different endpoints), so the C# 5-strike trap
  // never fired, and React-side calls just spun on "unauthenticated"
  // errors with no recovery path. Now the React layer has its own
  // strike counter and the same forced-logout response.
  useEffect(() => {
    const onRevoked = (e: Event) => {
      const detail = (e as CustomEvent<{ reason?: string }>).detail ?? {};
      notifications.show({
        color: 'orange',
        title: 'Session expired',
        message: 'Brain rejected your token 5 times in a row — sign in again. Your data is safe.',
        autoClose: 8000,
      });
      // clearAllAuth already ran inside api.ts before dispatch; here we
      // just flip the React state + tell C# host to clear registry copy.
      setToken(null);
      setUser(null);
      try { postToHost({ type: 'persist_signout' }); } catch { /* C# host absent → web browser; safe */ }
      void detail; // detail.reason for telemetry; not surfaced
    };
    window.addEventListener('ai-outlook:auth-revoked', onRevoked);
    return () => window.removeEventListener('ai-outlook:auth-revoked', onRevoked);
  }, []);
  // Round 32: Action Inbox is the new default landing — what do I need to do
  // now, not how many emails are indexed.
  const [activeTab, setActiveTab] = useState<string>('action');
  // Round 36: background tab-prefetch. When the user opens the addin and
  // sits on the default Action tab, every other tab is cold — first click
  // triggers a fresh fetch with a noticeable spinner. The brain's
  // EndpointCache (round 31) holds responses for 5-10 min server-side,
  // so we can fire the high-value GET endpoints in the background once
  // the user is logged in. Next click hits a warm cache → instant render.
  // Fires once per login session, 4s after login (lets the active tab
  // finish its own fetch first so we don't compete on connection pool).
  useEffect(() => {
    if (!token || !user) return;
    const t = setTimeout(() => {
      // Each of these is a no-op if the brain's per-account cache is hot;
      // otherwise it primes the cache. .catch swallow keeps any one
      // endpoint failing from cancelling the rest.
      const prefetches: Array<Promise<unknown>> = [
        api.addinActionInbox5State().catch(() => null),
        api.addinMorningNarrative().catch(() => null),
        api.addinFolderCounts().catch(() => null),
        api.addinPeopleTop().catch(() => null),
        api.addinFollowups(3).catch(() => null),
        api.addinRiskFeed(14, 30, 0).catch(() => null),
        api.addinStats().catch(() => null),
        api.addinMorningBrief().catch(() => null),
      ];
      void Promise.allSettled(prefetches);
    }, 4_000);
    return () => clearTimeout(t);
  }, [token, user]);
  // Round 34: cross-tab UI state — sender drawer, command bar, search filter.
  const [senderDrawer, setSenderDrawer] = useState<string | null>(null);
  const [commandBarOpen, setCommandBarOpen] = useState(false);
  const [palOpen, setPalOpen] = useState(false);
  const [pendingSearchSeed, setPendingSearchSeed] = useState<{
    q?: string;
    tier?: string;
    sender?: string;
    since?: string;
  } | null>(null);
  const [outlookEmail, setOutlookEmail] = useState<OutlookEmailContext | null>(null);
  const [indexState, setIndexState] = useState<IndexState | null>(null);
  const [progress, setProgressRaw] = useState<IndexProgress | null>(null);
  // Round 29: progress events from the addin during bulk-index can fire
  // 10-30 times per second (one per chunk processed). Each call hits the
  // parent setState here, which re-renders the entire visited tab tree —
  // including Insights' Recharts charts. On a fast index this pegs the
  // WebView2 CPU and freezes the UI for the whole laptop.
  //
  // Throttle to ~3 updates per second (every 333ms). The progress
  // numbers don't need to update faster than the eye can see; the
  // bottleneck was React reconciliation, not data freshness.
  //
  // Pass-through for null (clearing progress is always immediate so
  // the UI doesn't lag after index_complete). Latest message wins.
  const _progressThrottleRef = useRef<{ ts: number; pending: IndexProgress | null; timer: ReturnType<typeof setTimeout> | null }>({ ts: 0, pending: null, timer: null });
  const setProgress = useCallback((updater: IndexProgress | null | ((prev: IndexProgress | null) => IndexProgress | null)) => {
    // Function-form updates and null clears go through immediately.
    if (typeof updater === 'function' || updater === null) {
      setProgressRaw(updater);
      return;
    }
    const now = Date.now();
    const ref = _progressThrottleRef.current;
    const elapsed = now - ref.ts;
    if (elapsed >= 333) {
      ref.ts = now;
      ref.pending = null;
      if (ref.timer) { clearTimeout(ref.timer); ref.timer = null; }
      setProgressRaw(updater);
      return;
    }
    // Within throttle window — coalesce. Latest msg overwrites pending,
    // timer flushes after the remaining window.
    ref.pending = updater;
    if (!ref.timer) {
      ref.timer = setTimeout(() => {
        const r = _progressThrottleRef.current;
        if (r.pending !== null) {
          r.ts = Date.now();
          setProgressRaw(r.pending);
          r.pending = null;
        }
        r.timer = null;
      }, 333 - elapsed);
    }
  }, []);
  // Sidebar badge data — count of overdue self-made promises. Polled.
  const [overduePromisesCount, setOverduePromisesCount] = useState(0);
  // Round 28-L Phase 1.3b: pending+fired reminder count for sidebar badge.
  const [reminderBadge, setReminderBadge] = useState<{ pending: number; fired: number }>({ pending: 0, fired: 0 });
  // Round 28-M Phase 2: AI overlay drawer state.
  const [askAi, setAskAi] = useState<{ message_id: string; subject?: string } | null>(null);
  const [draftAi, setDraftAi] = useState<{ message_id: string; subject?: string } | null>(null);
  const [events, setEvents] = useState<BridgeEvent[]>([]);
  const [outlookFolders, setOutlookFolders] = useState<BridgeBus['outlookFolders']>([]);
  const [indexPrep, setIndexPrep] = useState<{ folder: string; message: string } | null>(null);
  // Per-folder "last sync" timestamp. Distinct from `state.current_folder.last_indexed`,
  // which is the date of the NEWEST EMAIL in the folder (a CONTENT metric — doesn't
  // change when an incremental run finds 0 new mail). `lastSyncedByFolder[path]` is
  // the wall-clock time of the last successful index run, regardless of how many
  // emails it found. Updated on every `index_complete` event (incl. zero-result
  // incremental). User-visible label uses this so "I just clicked Index" matches the
  // displayed "synced just now" instead of "newest mail X min ago".
  const [lastSyncedByFolder, setLastSyncedByFolder] = useState<Record<string, number>>({});
  // Round 29: active broadcasts (superadmin → all users). Polled on
  // mount + every 60s. Critical-severity broadcasts render as a
  // blocking modal; info/warning render as a banner above the
  // navbar. Dismissable broadcasts can be hidden per-user.
  type Broadcast = Awaited<ReturnType<typeof api.broadcastsActive>>['broadcasts'][0];
  const [broadcasts, setBroadcasts] = useState<Broadcast[]>([]);
  // Round 29 / v3.5.55: Organize-with-AI modal state.
  const [organizeOpen, setOrganizeOpen] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const r = await api.broadcastsActive();
        if (!cancelled) setBroadcasts(r.broadcasts);
      } catch { /* ignore — not critical */ }
    };
    load();
    // 120s poll. Broadcasts are admin announcements pushed infrequently;
    // a 2-minute lag is acceptable. Cuts /addin/broadcasts/active traffic
    // in half across all active clients.
    const id = setInterval(load, 120_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  async function dismissBroadcast(id: string): Promise<void> {
    setBroadcasts((prev) => prev.filter((b) => b.id !== id));
    try { await api.broadcastDismiss(id); } catch { /* server-side dismiss may retry on next load */ }
  }

  // 2026-05-23: poll the brain's bulk-index queue depth. The COM addin
  // POSTs to /addin/index/bulk and gets 202 immediately (the brain queues
  // batches for background processing since v3.5.59), but the dashboard
  // previously showed "inserted: 1000" as if the rows were live the moment
  // the POST returned. Users got confused — refresh the addin tab and the
  // search showed zero hits even though the dashboard said indexing was
  // done. Now we surface real queue depth: pending_emails (still waiting
  // to be inserted) + processing_batches (workers currently running).
  // Two polling rates: fast (3s) while there's work pending, slow (30s)
  // when idle to catch new bursts cheaply.
  const [bulkQueueStatus, setBulkQueueStatus] = useState<BulkQueueStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async (): Promise<void> => {
      try {
        const r = await api.addinIndexBulkStatus();
        if (cancelled) return;
        setBulkQueueStatus(r);
        const busy = r.user.pending_emails > 0 || r.user.pending_batches > 0 || r.user.processing_batches > 0;
        timer = setTimeout(tick, busy ? 3_000 : 30_000);
      } catch {
        // Brain unreachable / 401 during sign-in — back off and retry.
        if (!cancelled) timer = setTimeout(tick, 30_000);
      }
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  // 3.5.46: per-folder MAIL-only count (excludes Calendar/NDR/hidden/Post
  // items the IPM.Note filter correctly skips). Populated by the addin's
  // index_complete event when present. Coverage card uses this in
  // preference to outlook_folders[].item_count so the X/Y(%) ratio
  // reflects mail-only-indexed / mail-only-total instead of
  // mail-indexed / all-items-including-non-mail.
  const [mailCountByFolder, setMailCountByFolder] = useState<Record<string, number>>({});
  // Welcome splash — May 12: dismisses on a fixed 1500ms timer (or earlier
  // if 8 critical-path endpoints respond first). Prefetches still fire to
  // warm caches, but DO NOT gate splash dismissal — they continue in the
  // background while the user already sees the UI. Previously a 4200ms
  // minimum splash kept users waiting 3+ extra seconds even on a fully
  // warm brain.
  const [splashVisible, setSplashVisible] = useState(true);
  const [splashStage, setSplashStage] = useState<'greeting' | 'loading' | 'almost' | 'slow'>('greeting');
  useEffect(() => {
    // Stage transitions purely for visual feedback if splash lingers.
    const stageT1 = setTimeout(() => setSplashStage('loading'), 800);
    const stageT2 = setTimeout(() => setSplashStage('almost'), 1500);
    const stageT3 = setTimeout(() => setSplashStage('slow'), 4000);
    // Fire-and-forget STAGED prefetches — populate brain L2 caches for
    // every panel so user-facing tab clicks are sub-second. Splash does
    // NOT wait for them. Each wave swallows errors (best-effort warming).
    //
    // Wave 1 (now): 8 critical-path panels (Briefing, Action Inbox,
    //   Cases, People, Folder Counts, Reminders, Risk Feed, Patterns).
    // Wave 2 (+2s): heavier panels user might click within seconds.
    // Wave 3 (+5s): rare/secondary panels.
    //
    // Staggering keeps the brain from being hammered with 15 simultaneous
    // requests on every addin open (which during my testing caused 85s
    // cold-path bursts).
    // May 2026: split Wave 1 into two sub-waves of 4 (with a 400 ms gap)
    // instead of 8 simultaneous. The brain runs better-sqlite3 synchronously
    // on a single Node event loop; 8 parallel handlers can serialize behind
    // any single slow query (risk-feed, action-inbox-5state cold paths).
    // Even with the startup pre-warmer, on first-time users the brain has
    // no cached briefing yet — and Caddy round-robins so half hit blue,
    // half hit green, doubling the cold-cache contention. Sub-wave 1A
    // covers what the default tab (Briefing) needs first.
    void Promise.allSettled([
      api.addinActionInbox5State(),
      api.addinCasesList(),
      api.addinFolderCounts().catch(() => null),
      api.addinRiskFeed(14, 30, 0).catch(() => null),
    ]);
    const wave1bT = setTimeout(() => {
      void Promise.allSettled([
        api.addinPeopleTop().catch(() => null),
        api.addinRemindersList('all').catch(() => null),
        api.addinPatterns().catch(() => null),
        api.addinCasesMembership().catch(() => null),
      ]);
    }, 400);
    const wave2T = setTimeout(() => {
      void Promise.allSettled([
        api.addinActionInbox().catch(() => null),
        api.addinMorning().catch(() => null),
        api.addinPromisesList('pending').catch(() => null),
        api.addinStats().catch(() => null),
      ]);
    }, 2000);
    const wave3T = setTimeout(() => {
      void Promise.allSettled([
        api.addinMorningNarrative().catch(() => null),
        api.addinCloseout().catch(() => null),
        api.addinIndexHealth().catch(() => null),
      ]);
    }, 5000);
    // Dismiss after a short fixed window — long enough for first paint
    // of the underlying UI, short enough to feel snappy. Visual feedback
    // stages above kick in if anything keeps the user on splash.
    const dismissT = setTimeout(() => setSplashVisible(false), 1500);
    return () => {
      clearTimeout(stageT1);
      clearTimeout(stageT2);
      clearTimeout(stageT3);
      clearTimeout(dismissT);
      clearTimeout(wave1bT);
      clearTimeout(wave2T);
      clearTimeout(wave3T);
    };
  }, []);
  // Round 28-U: COM addin version reported by the bridge on init. Used so
  // user + dev can confirm "yes, you're running 3.5.17" at a glance from
  // the Settings tab.
  const [addinVersion, setAddinVersion] = useState<string | null>(null);
  const [outlookFoldersStatus, setOutlookFoldersStatus] = useState<BridgeBus['outlookFoldersStatus']>({
    received: false,
    error: null,
  });
  // Round 28-AC Phase B: in-addin auto-update state. The COM addin polls
  // /addin/release on startup + hourly and pushes one of:
  //   update_available  (soft prompt — user can dismiss)
  //   update_required   (force — full-screen blocker, no dismiss)
  //   update_progress   (during downloading / extracting / installing)
  type UpdateInfo = {
    installed_version: string;
    latest_version: string;
    download_url: string;
    sha256: string;
    size_bytes: number;
    changelog: string[];
    forced: boolean;
    force_below_version?: string | null;
  };
  type UpdateProgress = {
    phase: 'downloading' | 'verifying' | 'extracting' | 'starting' | 'error';
    percent?: number;
    downloaded?: number;
    total?: number;
    message?: string;
  };
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [updateProgress, setUpdateProgress] = useState<UpdateProgress | null>(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);

  const pushEvent = (e: BridgeEvent) => setEvents((prev) => [e, ...prev].slice(0, 50));

  // Pull initial action from URL hash so the host can deep-link, e.g.
  // #triage to land directly on the triage tab.
  useEffect(() => {
    const h = window.location.hash.replace('#', '');
    if (
      [
        'action',
        'insights',
        'triage',
        'search',
        'brief',
        'followups',
        'cases',
        'rules',
        'audit',
        'index',
        'settings',
        'reminders',
        'templates',
        'goals',
        'autopilot',
      ].includes(h)
    )
      setActiveTab(h);
  }, []);

  // Search-row case badge clicks set sessionStorage and dispatch this event.
  // We listen here so we can switch to the Cases tab — CasesPanel itself
  // listens for the same event and auto-opens the named case.
  useEffect(() => {
    const handler = () => setActiveTab('cases');
    window.addEventListener('ai-outlook:open-case', handler);
    return () => window.removeEventListener('ai-outlook:open-case', handler);
  }, []);

  // Brief-state-badge clicks — switch to Action Inbox.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      if (typeof detail === 'string') setActiveTab(detail);
    };
    window.addEventListener('ai-outlook:switch-tab', handler);
    return () => window.removeEventListener('ai-outlook:switch-tab', handler);
  }, []);

  // Poll overdue-promises count for the sidebar badge. Runs on mount and
  // every 5 minutes; cheap query against already-loaded promises.
  useEffect(() => {
    let cancelled = false;
    function tick() {
      api.addinPromisesList('pending').then((r) => {
        if (cancelled) return;
        const now = Date.now();
        const overdue = r.promises.filter((p) =>
          p.maker === 'me' && p.due_at && new Date(p.due_at).getTime() < now,
        ).length;
        setOverduePromisesCount(overdue);
      }).catch(() => { /* silent */ });
    }
    tick();
    const id = setInterval(tick, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Round 28-M Phase 2: open Ask AI drawer when right-click dispatches event.
  useEffect(() => {
    const askHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ message_id: string; subject?: string }>).detail;
      if (detail?.message_id) setAskAi(detail);
    };
    const draftHandler = (e: Event) => {
      const detail = (e as CustomEvent<{ message_id: string; subject?: string }>).detail;
      if (detail?.message_id) setDraftAi(detail);
    };
    window.addEventListener('ai-outlook:ask-ai', askHandler);
    window.addEventListener('ai-outlook:draft-reply', draftHandler);
    return () => {
      window.removeEventListener('ai-outlook:ask-ai', askHandler);
      window.removeEventListener('ai-outlook:draft-reply', draftHandler);
    };
  }, []);

  // Round 28-L Phase 1.3b: poll reminder counts for sidebar badge.
  // Polls every minute since reminders fire at minute granularity.
  useEffect(() => {
    let cancelled = false;
    function tick() {
      api.addinRemindersList('all').then((r) => {
        if (cancelled) return;
        const pending = r.reminders.filter((x) => x.status === 'pending').length;
        const fired = r.reminders.filter((x) => x.status === 'fired').length;
        setReminderBadge({ pending, fired });
      }).catch(() => { /* silent */ });
    }
    tick();
    const id = setInterval(tick, 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Round 29: keyboard shortcuts. Ctrl+K → Search. 1..6 → tab jumps when
  // not typing in an input.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Ignore when user is typing in a form field.
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      const inField = tag === 'input' || tag === 'textarea';
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        // Round 34: ⌘K opens the AI command bar modal — natural language → search filters.
        setCommandBarOpen(true);
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        setPalOpen(true);
      }
      if (!inField && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const tabs = [
          'action',
          'insights',
          'search',
          'triage',
          'brief',
          'followups',
          'cases',
          'rules',
          'audit',
          'index',
          'settings',
        ];
        const n = Number.parseInt(e.key, 10);
        const tab = tabs[n - 1];
        if (n >= 1 && tab !== undefined) {
          setActiveTab(tab);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Bridge listener — receives messages from C# host.
  useEffect(() => {
    const handler = (e: { data: unknown }) => {
      const msg = parseHostMessage(e.data);
      if (!msg) return;
      switch (msg.type) {
        case 'set_email':
          setOutlookEmail(msg.email);
          break;
        case 'set_token':
          if (msg.token) {
            setStoredToken(msg.token);
            setToken(msg.token);
            if (msg.user) {
              setStoredUser(msg.user);
              setUser(msg.user);
            }
          }
          break;
        case 'reload':
          window.location.reload();
          break;
        case 'index_state':
          setIndexState(msg);
          // Reconcile progress with the host's authoritative running flag.
          // The host's _indexRunning persists across React re-mounts (e.g.
          // if the user closes the WebView pane and reopens it, or after
          // a dashboard hot-reload). Without this, React boots with
          // progress=null, the user sees "Index this folder" buttons,
          // and a click is rejected with "Index already running" because
          // the C# side is still chugging on an orphan task. Now: if
          // host says running=true but we have no progress, materialize
          // a placeholder so the Pause/Cancel buttons render. Real
          // numbers fill in on the next index_progress tick.
          if (msg.running) {
            setProgress((p) => p ?? {
              processed: 0,
              total: 0,
              inserted: 0,
              duplicates_skipped: 0,
              skipped: 0,
              rate_per_sec: 0,
              elapsed_sec: 0,
              eta_sec: 0,
            });
          } else {
            // Host says nothing's running — clear any stale progress UI
            // (e.g. a previous run finished while the WebView was hidden,
            // and we missed the index_complete event because the bridge
            // wasn't listening).
            setProgress(null);
            setIndexPrep(null);
          }
          break;
        case 'index_preparing':
          // Round 46: surface the prep phase so big folders don't look frozen.
          setIndexPrep({ folder: msg.folder_name, message: msg.message });
          break;
        case 'index_started':
          setIndexPrep(null);
          setProgress({
            processed: 0,
            total: msg.total,
            inserted: 0,
            duplicates_skipped: 0,
            skipped: 0,
            rate_per_sec: 0,
            elapsed_sec: 0,
            eta_sec: 0,
          });
          pushEvent({
            ts: Date.now(),
            level: 'info',
            message: `Index started on "${msg.folder_name}" (${msg.total.toLocaleString()} ${msg.incremental ? 'new' : 'total'} items)`,
          });
          break;
        case 'index_progress':
          setProgress(msg);
          break;
        case 'index_complete':
          setProgress(null);
          setIndexPrep(null);
          // Record per-folder sync time so the UI can show "synced just now"
          // even when no new mail was found. Without this, an incremental run
          // that returns 0 new (because the folder hasn't received mail since
          // the last sync) leaves the displayed "newest mail X ago" stale —
          // user thinks the click did nothing.
          if (msg.folder) {
            setLastSyncedByFolder((prev) => ({ ...prev, [msg.folder]: Date.now() }));
          }
          // 3.5.46: capture mail-only count for the coverage card.
          // biome-ignore lint/suspicious/noExplicitAny: addin sends extended fields not in the union type
          const extMsg = msg as any;
          if (msg.folder && typeof extMsg.mail_item_count === 'number' && extMsg.mail_item_count > 0) {
            setMailCountByFolder((prev) => ({ ...prev, [msg.folder]: extMsg.mail_item_count }));
          }
          // Distinguish "0 new = up to date" from "found N new" so the toast is meaningful.
          if ((msg.inserted ?? 0) === 0 && (msg.duplicates_skipped ?? 0) === 0 && (msg.skipped ?? 0) === 0) {
            pushEvent({
              ts: Date.now(),
              level: 'success',
              message: `Up to date — no new mail in "${msg.folder ?? 'this folder'}" since last sync.`,
            });
          } else {
            pushEvent({
              ts: Date.now(),
              level: 'success',
              message: `Done. ${msg.inserted.toLocaleString()} new, ${msg.duplicates_skipped.toLocaleString()} duplicates, ${msg.skipped.toLocaleString()} skipped — ${formatDuration(msg.elapsed_ms / 1000)}`,
            });
          }
          break;
        case 'index_cancelled':
          setProgress(null);
          setIndexPrep(null);
          pushEvent({ ts: Date.now(), level: 'warn', message: 'Index cancelled.' });
          break;
        case 'index_error':
          // Special case: if the host says "Index already running", DON'T
          // clear our progress UI — that would hide the very controls
          // (Pause/Cancel) the user needs to resolve the situation.
          // Instead, re-request the host's authoritative state via the
          // 'ready' bridge call, which triggers PushIndexState. The
          // index_state handler then materializes a placeholder progress
          // so the user can act.
          if (/already running/i.test(msg.error)) {
            postToHost({ type: 'ready' });
            pushEvent({ ts: Date.now(), level: 'warn', message: 'Index already running on the host — syncing state.' });
            break;
          }
          setProgress(null);
          setIndexPrep(null);
          pushEvent({ ts: Date.now(), level: 'error', message: msg.error });
          break;
        case 'live_index':
          if (msg.inserted > 0)
            pushEvent({
              ts: Date.now(),
              level: 'success',
              message: `Live index: ${msg.inserted} new email${msg.inserted === 1 ? '' : 's'} added.`,
            });
          break;
        // 3.5.10: surface C# action errors to the user. Pre-3.5.10 the
        // optimistic UI showed "Deleted from Outlook" toasts immediately,
        // and if the C# resolution failed (most common when a search hit
        // had a non-EntryID message_id) the user never knew. Now the
        // failure shows as a red toast.
        case 'action_error':
          notifications.show({
            color: 'red',
            title: msg.action ? `${msg.action} failed` : 'Outlook action failed',
            message: msg.error || 'The addin could not complete this action in Outlook.',
            autoClose: 6000,
          });
          break;
        case 'action_success':
          // Optimistic UI already showed the success toast — no need to double up.
          break;
        // 3.5.12: surface the post-index body-fill pass to the user.
        case 'body_fill_started':
          pushEvent({
            ts: Date.now(),
            level: 'info',
            message: `Filling email bodies in background (${msg.remaining_total.toLocaleString()} to process). Outlook may feel slow during this. You can keep using it.`,
          });
          break;
        case 'body_fill_progress':
          pushEvent({
            ts: Date.now(),
            level: 'info',
            message: `Body-fill: ${msg.processed.toLocaleString()} processed, ${msg.repaired.toLocaleString()} updated.`,
          });
          break;
        case 'body_fill_complete':
          pushEvent({
            ts: Date.now(),
            level: 'success',
            message: `Body-fill done: ${msg.processed.toLocaleString()} processed, ${msg.repaired.toLocaleString()} bodies filled in ${formatDuration(msg.elapsed_ms / 1000)}.`,
          });
          break;
        case 'body_fill_cancelled':
          pushEvent({
            ts: Date.now(),
            level: 'warn',
            message: `Body-fill cancelled at ${msg.processed.toLocaleString()} items (${msg.repaired.toLocaleString()} updated). Re-run "Index All" to resume.`,
          });
          break;
        case 'body_fill_skipped':
          // No empty bodies to fill — quiet success, no toast needed.
          break;
        case 'index_cache_warning':
          // 3.5.13: explain the slow path. Without this the user just sees
          // an unexplained slowdown and thinks the addin is broken.
          pushEvent({
            ts: Date.now(),
            level: 'warn',
            message: `${msg.folder_name}: ${msg.message}`,
          });
          break;
        case 'index_all_started':
          pushEvent({
            ts: Date.now(),
            level: 'info',
            message: `Indexing ${msg.folder_count} folder${msg.folder_count === 1 ? '' : 's'} across all your Outlook mailboxes…`,
          });
          break;
        case 'index_all_folder':
          pushEvent({
            ts: Date.now(),
            level: 'info',
            message: `Folder ${msg.folder_index}/${msg.folder_count}: ${msg.folder_name}`,
          });
          break;
        case 'index_all_complete':
          pushEvent({
            ts: Date.now(),
            level: 'success',
            message: `Done — processed ${msg.folders_processed} folder${msg.folders_processed === 1 ? '' : 's'} across all your mailboxes.`,
          });
          break;
        case 'outlook_folders':
          setOutlookFolders(msg.folders ?? []);
          setOutlookFoldersStatus({ received: true, error: msg.error ?? null });
          break;
        case 'addin_version':
          if (typeof msg.version === 'string') setAddinVersion(msg.version);
          break;
        case 'auth_expired':
          // Round 38 v3.6.2 (2026-06-20): rabih reported "unauthenticated
          // errors everywhere" after auto-updating to 3.6.1. Root cause:
          // his 30-day access token had expired (last login 36 days ago)
          // and the previous handler here just popped a toast saying "try
          // again" without giving any path to recovery. The addin was
          // stuck — every API call 401'd, the user had nowhere to sign in.
          //
          // New behavior: when the C# host pushes auth_expired (set after
          // 5 consecutive 401s clear the stored token), we treat it the
          // same as an explicit sign-out — clear local auth state, push
          // persist_signout to clear the C#-side registry token, flip
          // token state to null which immediately re-renders this page
          // as <AddinLogin>. The user is one tap away from re-auth, in
          // the same pane, no DevTools / browser detour required.
          notifications.show({
            color: 'orange',
            title: 'Session expired',
            message: msg.phase === 'periodic_check'
              ? 'Your sign-in expired in the background. Please sign in again — your data is safe.'
              : 'Brain rejected your token (likely 30+ days since last sign-in). Please sign in again — your data is safe.',
            autoClose: 8000,
          });
          clearAllAuth();
          setToken(null);
          setUser(null);
          // Tell the C# host to clear its DPAPI-stored token too. Without
          // this, reopening the pane re-reads the same expired token and
          // we loop right back into auth_expired land.
          postToHost({ type: 'persist_signout' });
          break;
        case 'index_paused':
          // Eagerly flip the progress.paused flag so the button toggles to
          // "Resume" immediately on click. Without this, the UI waits for
          // the next index_progress message from the host — which during a
          // slow bulk batch can be 20-30 seconds away, making the user
          // think the Pause click did nothing.
          pushEvent({ ts: Date.now(), level: 'info', message: 'Indexing paused.' });
          setProgress((p) => (p ? { ...p, paused: true } : p));
          break;
        case 'index_resumed':
          pushEvent({ ts: Date.now(), level: 'info', message: 'Indexing resumed.' });
          setProgress((p) => (p ? { ...p, paused: false } : p));
          break;
        case 'auto_index_tick':
          pushEvent({ ts: Date.now(), level: 'info', message: `Auto-index fired: ${msg.target}` });
          break;
        // Round 28-AC Phase B: addin auto-update events from the COM host.
        case 'update_available':
          setUpdateInfo({
            installed_version: msg.installed_version,
            latest_version: msg.latest_version,
            download_url: msg.download_url,
            sha256: msg.sha256,
            size_bytes: msg.size_bytes ?? 0,
            changelog: Array.isArray(msg.changelog) ? msg.changelog : [],
            forced: false,
          });
          setUpdateDismissed(false);
          break;
        case 'update_required':
          setUpdateInfo({
            installed_version: msg.installed_version,
            latest_version: msg.latest_version,
            download_url: msg.download_url,
            sha256: msg.sha256,
            size_bytes: msg.size_bytes ?? 0,
            changelog: Array.isArray(msg.changelog) ? msg.changelog : [],
            forced: true,
            ...(msg.force_below_version !== undefined ? { force_below_version: msg.force_below_version } : {}),
          });
          setUpdateDismissed(false);
          break;
        case 'update_progress':
          setUpdateProgress({
            phase: msg.phase,
            ...(msg.percent !== undefined ? { percent: msg.percent } : {}),
            ...(msg.downloaded !== undefined ? { downloaded: msg.downloaded } : {}),
            ...(msg.total !== undefined ? { total: msg.total } : {}),
            ...(msg.message !== undefined ? { message: msg.message } : {}),
          });
          if (msg.phase === 'error') {
            notifications.show({
              color: 'red',
              title: 'Update failed',
              message: msg.message ?? 'Could not install the update.',
              autoClose: 12_000,
            });
          }
          break;
      }
    };
    window.chrome?.webview?.addEventListener('message', handler);
    postToHost({ type: 'ready' });
    return () => {
      // No removeEventListener API on chrome.webview; harmless to leak in addin context.
    };
  }, []);

  if (!token || !user) {
    // Round 42 audit fix: force-update overlay must render even at the login
    // screen. Pre-fix: users on ≤3.6.4 whose addin fired `update_required`
    // saw the modal state get set BUT the overlay JSX lived below this early
    // return — meaning if their session had lapsed (v3.6.2's aggressive
    // 5x-401 auto-logout was exactly that vector) they were stuck seeing
    // AddinLogin with no way to update. Now: overlay renders as a sibling of
    // AddinLogin so the force-update path works regardless of auth state.
    return (
      <>
        {updateInfo?.forced && (
          <UpdateBlockerOverlay info={updateInfo} progress={updateProgress} />
        )}
        <AddinLogin
          onSuccess={(u) => {
            setToken(getStoredToken());
            setUser(u);
          }}
        />
      </>
    );
  }

  const onSignOut = async () => {
    try {
      await api.authLogout();
    } catch {
      /* ignore */
    } finally {
      clearAllAuth();
      setToken(null);
      setUser(null);
      // Round 28: also clear C#-side stored token so reopen prompts again.
      postToHost({ type: 'persist_signout' });
    }
  };

  // Round 44: backward-compat for old tab keys + the new sidebar names.
  // Old → new: radar→changes, discover→suggestions, audit→activity, index→sources.
  const tab = (
    activeTab === 'radar' ? 'changes' :
    activeTab === 'discover' ? 'suggestions' :
    activeTab === 'audit' ? 'activity' :
    activeTab === 'index' ? 'sources' :
    activeTab
  );

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 240, breakpoint: 'sm' }}
      padding="md"
    >
      {/* Round 28-AC Phase B: addin self-update UI. Two surfaces:
          1. Soft prompt — Mantine Modal, dismissible, shows when
             update_available fires.
          2. Force overlay — full-screen blocking div, NO close button,
             shown when update_required fires (installed < force_below).
          During download/install both render the progress bar. */}
      {updateInfo?.forced && (
        <UpdateBlockerOverlay
          info={updateInfo}
          progress={updateProgress}
        />
      )}
      {/* 3.5.46: don't auto-popup the modal while the welcome splash is
          still up — the splash overlays everything z-index 9000 so the
          Mantine Modal opens BEHIND it and the user never sees it. After
          the splash dismisses, the modal opens normally. The header's
          manual Update button is the always-available fallback. */}
      <UpdateAvailableModal
        info={updateInfo && !updateInfo.forced && !splashVisible ? updateInfo : null}
        progress={updateProgress}
        dismissed={updateDismissed}
        onDismiss={() => setUpdateDismissed(true)}
      />
      {senderDrawer && (
        <SenderProfileDrawer
          email={senderDrawer}
          onClose={() => setSenderDrawer(null)}
          onJumpToSearch={(filter) => {
            setPendingSearchSeed(filter);
            setActiveTab('search');
            setSenderDrawer(null);
          }}
        />
      )}
      {askAi && (
        <AskAiDrawer
          message_id={askAi.message_id}
          {...(askAi.subject !== undefined ? { subject: askAi.subject } : {})}
          onClose={() => setAskAi(null)}
        />
      )}
      {draftAi && (
        <DraftReplyDrawer
          message_id={draftAi.message_id}
          {...(draftAi.subject !== undefined ? { subject: draftAi.subject } : {})}
          onClose={() => setDraftAi(null)}
        />
      )}
      <CommandBar
        opened={commandBarOpen}
        onClose={() => setCommandBarOpen(false)}
        onSubmitFilter={(filter) => {
          setPendingSearchSeed(filter);
          setActiveTab('search');
          setCommandBarOpen(false);
        }}
      />
      <OrganizeWithAIModal
        opened={organizeOpen}
        onClose={() => setOrganizeOpen(false)}
      />
      <QuickPalette
        opened={palOpen}
        onClose={() => setPalOpen(false)}
        onPick={(action) => {
          setPalOpen(false);
          if (action.kind === 'tab') setActiveTab(action.value);
          else if (action.kind === 'case') {
            sessionStorage.setItem('addin_open_case', action.value);
            setActiveTab('cases');
            window.dispatchEvent(new Event('ai-outlook:open-case'));
          } else if (action.kind === 'preset') {
            setPendingSearchSeed({
              q: action.payload.q,
              ...(action.payload.filter !== 'all' ? { tier: action.payload.filter } : {}),
              ...(action.payload.sender ? { sender: action.payload.sender } : {}),
            });
            setActiveTab('search');
          }
        }}
      />

      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" gap="md" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Box
              w={32}
              h={32}
              style={{
                borderRadius: 8,
                background: 'linear-gradient(135deg, #0f6cbd, #0078d4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 700,
                fontSize: 13,
              }}
            >
              ai
            </Box>
            <Stack gap={0}>
              <Group gap={6}>
                <Text fw={700} size="sm">ai-outlook</Text>
                <PrivateModeBadge />
              </Group>
              <Text size="xs" c="dimmed">{user.email}</Text>
            </Stack>
          </Group>

          <GlobalSearchInput
            onSubmit={(q, mode) => {
              if (mode === 'ask') {
                setActiveTab('ask');
                window.dispatchEvent(new CustomEvent('ai-outlook:ask-prefill', { detail: q }));
              } else {
                setPendingSearchSeed({ q });
                setActiveTab('search');
              }
            }}
            onOpenCommandBar={() => setCommandBarOpen(true)}
          />

          <Group gap="xs" wrap="nowrap">
            {/* Round 29 / v3.5.55: Organize-with-AI launcher in header. */}
            <Tooltip label="Organize emails with AI — type a request, preview, confirm">
              <Button
                size="xs"
                variant="light"
                color="violet"
                onClick={() => setOrganizeOpen(true)}
                leftSection={<IconSparkles {...ICON_PROPS} />}
              >
                Organize
              </Button>
            </Tooltip>
            <NotificationHistoryButton />
            {indexState?.new_mail_hooked && (
              <Tooltip label="Live indexing is on — new mail is automatically indexed">
                <Badge variant="light" color="green" leftSection={<span>●</span>}>live</Badge>
              </Tooltip>
            )}
            {indexState && indexState.auto_index_minutes > 0 && (
              <Tooltip label={`Auto-index every ${indexState.auto_index_minutes} min`}>
                <Badge variant="light" color="blue">auto · {indexState.auto_index_minutes}m</Badge>
              </Tooltip>
            )}
            {/* 3.5.46: manual Update button — visible whenever an update
                is available, so the user can re-open the prompt at any
                time. Auto-popup may collide with the welcome splash and
                get missed; this gives a stable retry path. */}
            {updateInfo && (
              <Tooltip label={`Update to v${updateInfo.latest_version}`}>
                <Button
                  size="xs"
                  variant="filled"
                  color="blue"
                  onClick={() => setUpdateDismissed(false)}
                  leftSection={<IconDownload {...ICON_PROPS} />}
                >
                  Update v{updateInfo.latest_version}
                </Button>
              </Tooltip>
            )}
            <Tooltip label="Sign out">
              <ActionIcon variant="subtle" onClick={onSignOut} aria-label="sign out">
                <IconX {...ICON_PROPS} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        <Sidebar activeTab={tab} setActiveTab={setActiveTab} indexRunning={!!indexState?.running} overduePromisesCount={overduePromisesCount} reminderBadge={reminderBadge} />
      </AppShell.Navbar>

      <AppShell.Main>
        {/*
          Keep-alive tab pattern: once a tab is visited, its panel stays
          mounted but is hidden via CSS (`display: none`) when inactive.
          React preserves the component's state, so re-visiting a tab is
          INSTANT — no remount, no useEffect refire, no re-fetch from the
          brain. Tabs that have never been visited are NOT mounted (avoids
          firing all panel APIs on first dashboard load).
          Why: prior pattern was conditional render `{tab === 'X' && <X/>}`
          which unmounts the inactive panel. Every click cycle re-mounted +
          re-fetched, and rapid clicks fired multiple parallel API calls
          that competed for the brain's already-saturated event loop —
          producing the "data not available" flicker the user reported.
        */}
        <TabKeepAlive active={tab} name="action">
          <TabErrorBoundary name="Action Inbox">
            <ActionInboxPanel onSwitchTab={setActiveTab} onOpenSender={(e) => setSenderDrawer(e)} />
          </TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="insights">
          <TabErrorBoundary name="Insights"><InsightsPanel onSwitchTab={setActiveTab} /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="search">
          <TabErrorBoundary name="Search">
            <SearchPanel
              onOpenSender={(e) => setSenderDrawer(e)}
              seedFilter={pendingSearchSeed}
              onSeedConsumed={() => setPendingSearchSeed(null)}
            />
          </TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="triage">
          <TabErrorBoundary name="Triage"><TriagePanel email={outlookEmail} /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="brief"><TabErrorBoundary name="Brief"><BriefPanel /></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="workspace"><TabErrorBoundary name="Workspace"><Suspense fallback={null}><CommandCenterPage /></Suspense></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="ask"><TabErrorBoundary name="Ask"><AskPanel /></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="changes"><TabErrorBoundary name="Changes"><RadarPanel /></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="suggestions">
          <TabErrorBoundary name="Suggestions"><DiscoverPanel /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="followups">
          <TabErrorBoundary name="Follow-ups"><FollowupsPanel /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="cases"><TabErrorBoundary name="Cases"><CasesPanel /></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="people">
          <TabErrorBoundary name="People"><PeoplePanel onOpenSender={(e) => setSenderDrawer(e)} /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="calendar">
          <TabErrorBoundary name="Calendar"><CalendarPanel /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="rules"><TabErrorBoundary name="Rules"><RulesPanel /></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="activity"><TabErrorBoundary name="Activity"><AuditPanel /></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="sources">
          <TabErrorBoundary name="Index">
            <IndexPanel
              state={indexState}
              progress={progress}
              prep={indexPrep}
              events={events}
              folders={outlookFolders}
              foldersStatus={outlookFoldersStatus}
              lastSyncedByFolder={lastSyncedByFolder}
              mailCountByFolder={mailCountByFolder}
              bulkQueueStatus={bulkQueueStatus}
            />
          </TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="settings">
          <TabErrorBoundary name="Settings"><SettingsPanel state={indexState} onSignOut={onSignOut} addinVersion={addinVersion} /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="reminders">
          <TabErrorBoundary name="Reminders"><ReminderPanel /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="templates">
          <TabErrorBoundary name="Templates"><ReplyTemplatesPanel /></TabErrorBoundary>
        </TabKeepAlive>
        <TabKeepAlive active={tab} name="goals"><TabErrorBoundary name="Goals"><GoalsPanel /></TabErrorBoundary></TabKeepAlive>
        <TabKeepAlive active={tab} name="autopilot">
          <TabErrorBoundary name="Autopilot"><AutopilotPanel /></TabErrorBoundary>
        </TabKeepAlive>
      </AppShell.Main>

      {/* Round 29: broadcasts. Critical = blocking modal; info/warning =
          banner pinned above the AppShell content. Hidden during splash
          so they don't double-stack with the welcome screen. */}
      {!splashVisible && broadcasts.length > 0 && (
        <BroadcastSurface
          broadcasts={broadcasts}
          onDismiss={dismissBroadcast}
        />
      )}
      {/* Welcome splash — fades in for ~1.8 s while panels pre-load in the
          background. Position fixed + zIndex 9000 covers everything but
          stays below the force-update overlay (zIndex 9999). */}
      {splashVisible && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9000,
            background: 'linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-indigo-7) 100%)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'opacity 300ms ease',
          }}
        >
          <Stack align="center" gap="md" style={{ maxWidth: 420, padding: '0 24px' }}>
            <Title order={2} c="white" ta="center">
              👋 Welcome back{user?.display_name ? `, ${user.display_name.split(' ')[0]}` : ''}
            </Title>
            <Text c="rgba(255,255,255,0.92)" size="sm" ta="center">
              {splashStage === 'greeting' && 'Getting things ready…'}
              {splashStage === 'loading' && 'Loading your inbox, action items, and contacts…'}
              {splashStage === 'almost' && 'Almost ready — fetching the latest data…'}
              {splashStage === 'slow' && 'Taking longer than usual. The brain is busy — your data is on the way.'}
            </Text>
            <Loader color="white" size="sm" />
            {splashStage === 'slow' && (
              <Text
                size="xs"
                style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.7)', textDecoration: 'underline' }}
                onClick={() => setSplashVisible(false)}
              >
                Skip and load anyway
              </Text>
            )}
          </Stack>
        </div>
      )}
    </AppShell>
  );
}

// ============================================================================
// Sidebar — grouped navigation (Round 44 redesign).
// Sections: Today, Find, Work, Intelligence, Automation, System.
// ============================================================================

// ⌘P spotlight-style quick palette: jump to a tab, open a case, or apply preset.
